import React, { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import fetchWithAuth from '../utils/fetchWithAuth'
import BackButton from '../components/ui/atoms/BackButton'
import { FaCheckCircle } from 'react-icons/fa'
// import { IoDiamond } from 'react-icons/io5'
import { useRevenueCat } from '../hooks/useRevenueCat'

// isNative?
const isNative = Capacitor.getPlatform() !== 'web'

interface PaywallProps {
  isOnboarding?: boolean;
  onBack?: () => void;
  onSubscribe?: () => void;
}

export default function Paywall({
  isOnboarding = false,
  onBack,
  onSubscribe,
}: PaywallProps) {  
  const navigate = useNavigate()
  
  const location = useLocation()
  const fromOnboarding = (location.state as any)?.fromOnboarding === true
  const { showToast } = useToast() as any
  const { backendUser, user: firebaseUser } = useAuth() as any
  const { initialized, offerings, customerInfo, purchasePackage } = useRevenueCat()
  const [loading, setLoading] = useState(false)

  const pkg = offerings?.current?.monthly

  // if already subscribed, bail
  useEffect(() => {
    if (backendUser?.isSubscribed) {
      navigate('/app/settings/')
    }
  }, [backendUser?.isSubscribed, navigate])

  const priceString = pkg?.product.priceString ?? '€1.99'
  // always stable label
  const trialText = 'Start Free 7-day Trial'

  // unified subscribe
  const handleSubscribe = async () => {
    if (!isNative) {
      setLoading(true)
      try {
        const res = await fetchWithAuth(
          '/api/subscription/create-checkout-session',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: firebaseUser.uid }),
          }
        )
        const { url } = await res
        window.location.href = url
      } catch (err: any) {
        console.error(err)
        showToast({ type: 'error', message: err.message || 'Failed to start subscription.' })
      } finally {
        setLoading(false)
      }
      return
    }

    if (!initialized || !pkg) {
      return showToast({ type: 'error', message: 'Store not ready—try again.' })
    }
    setLoading(true)
    try {
      // 1) Kick off the purchase
      await purchasePackage(pkg)

      // 2) Immediately navigate to your success page
      navigate('/app/subscribe/success')

      // 3) (Optional) webhook will mark them subscribed on the server.
      //    No need to await a non-existent /activate endpoint here.
    } catch (err: any) {
      console.error('Purchase error:', err)
      showToast({ type: 'error', message: (err as any).message || 'Purchase failed.' })
    } finally {
      setLoading(false)
    }
  }

  // show spinner until RevenueCat is ready and your package is loaded
  if (!initialized || !pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="loader" />
      </div>
    )
  }

  return (
    <div className="relative h-full flex flex-col bg-gray-50">

      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 right-4">
          <button
            onClick={() =>
              navigate('/app/subscribe/success', {
                state: { features: [
                  'Build your dietary profile…',
                  'Kick unwanted ingredients…',
                  'Turn on allergy alerts…',
                  'View detailed nutrition facts…',
                ] }
              })
            }
            className="text-xs px-2 py-1 bg-gray-200 rounded"
          >
            Preview Success
          </button>
        </div>
      )}

      {/* back button only when NOT launched from onboarding */}
      {!fromOnboarding && (
        <div className="absolute top-4 left-4 z-10">
          <BackButton onClick={() => navigate(-1)} />
        </div>
      )}

      {/* 1) HEADER IMAGE (in normal flow, just under the safe-area margin) */}
      <div className="flex-none h-[30vh] w-full">
        <img
          src="/paywall-header.jpg"
          alt="PanPals Premium"
          className="w-full h-full object-cover"
        />
      </div>

      {/* 2) WHITE CARD (contains title + scrollable perks) */}
      <div
        className={
          "relative flex-1 mx-auto w-full max-w-md bg-white " +
          "rounded-t-3xl px-6 pt-6"
        }
      >
        {/* 2b) PERKS (fills remaining card space, scrolls if overflow) */}
        <div className="flex-1 overflow-y-auto mt-4 mb-[calc(56px+env(safe-area-inset-bottom))]">
          <ul className="space-y-3">
            {[
              'Build your dietary profile: Pick diets, cuisines, occasion, and more.',
              'Kick unwanted ingredients to the curb so they never show up in your meal plan.',
              'Turn on allergy alerts and get a heads-up before you even open a recipe.',
              'View detailed nutrition facts for every recipe.',
            ].map((txt) => (
              <li key={txt} className="flex items-start gap-2">
                <FaCheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{txt}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 3) FIXED FOOTER: price, cancel text, CTA */}
      <div
        className="fixed inset-x-0 bottom-[3.5rem] z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Maybe Later link if we came from onboarding */}
        {fromOnboarding && (
          <div className="text-center mb-2">
            <button
              onClick={() => {
                // if the parent gave us an onBack callback, use it;
                // otherwise, just bail straight to home
                if (onBack) {
                  onBack()
                } else {
                  navigate('/app/home', { replace: true })
                }
              }}
              className="text-sm underline text-gray-600"
            >
              Skip for now
            </button>
          </div>
        )}
        <div className="bg-white px-2 pt-4 text-center mt-4">
          <p className="text-2xl font-bold text-gray-900">
            {priceString}/month
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Cancel anytime and refund for remainder of period.
          </p>
          <div className="px-6">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="mt-4 w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-medium disabled:opacity-50 transition"
            >
              {loading ? 'Processing…' : trialText}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}